import React, { CSSProperties, useCallback, useEffect, useMemo, useRef } from 'react'
import { useBoardContext } from '../../../hooks/use-board'
import { useTranslation } from 'react-i18next'
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { colorPrimary } from '../../../branding'
import { CSS } from '@dnd-kit/utilities'
import { Button, Card, Dropdown, Flex, Spin, Tooltip, Typography } from 'antd'
import { DeleteOutlined, EllipsisOutlined, LoadingOutlined } from '@ant-design/icons'
import { DropLinePosition } from './board'
import { MemoizedSortableCard } from './memoized-sortable-card'
import { DropCard } from './drop-card'
import { useArchiveCandidate, useCandidatesForColumnInfinite } from '../../../candidate/hooks'
import { useDeleteCampaignColumn, useSortColumnCandidates, useUpdateCampaignColumn } from '../../hooks'
import { useDeleteCompanyColumn, useSortCompanyColumnCandidates, useUpdateCompanyColumn } from '../../../company/hooks'
import { useAuth } from '../../../auth'

export const MemoizedSortableColumn = React.memo(
    function SortableColumn({
        id,
        title,
        isOver,
        dropLinePosition,
        height,
    }: {
        id: string
        title: string
        isOver: boolean
        dropLinePosition: DropLinePosition | null
        height?: CSSProperties['maxHeight']
    }) {
        const {
            showAttributes,
            campaignId,
            columnCandidates,
            setColumnCandidates,
            hasPendingOptimisticUpdate,
            activeItem,
        } = useBoardContext()
        const { activeCompany } = useAuth()
        const candidatesQuery = useCandidatesForColumnInfinite({ campaignId, columnId: id })
        const scrollContainerRef = useRef<HTMLDivElement>(null)

        // Combine all candidates from all pages
        const queryCandidates = useMemo(() => {
            return candidatesQuery.data?.pages.flatMap((page) => page.data) || []
        }, [candidatesQuery.data])

        // Sync query data to board context state - but NOT while there's a pending optimistic update
        useEffect(() => {
            if (hasPendingOptimisticUpdate) return // Don't sync during optimistic updates

            setColumnCandidates((prev) => ({
                ...prev,
                [id]: queryCandidates,
            }))
        }, [id, queryCandidates, setColumnCandidates, hasPendingOptimisticUpdate])

        // Get candidates from board state (includes optimistic updates)
        // Filter out the actively dragged item to prevent showing it in two places
        const items = useMemo(() => {
            const candidates = columnCandidates[id] || []
            if (activeItem) {
                return candidates.filter((c) => c.id !== activeItem.item.id)
            }
            return candidates
        }, [columnCandidates, id, activeItem])

        const hasNextPage = candidatesQuery.hasNextPage
        const isLoading = candidatesQuery.isFetching

        const handleScroll = useCallback(
            (event: Event) => {
                const container = event.target as HTMLElement
                if (!container || !hasNextPage || isLoading) return

                const { scrollTop, scrollHeight, clientHeight } = container
                const threshold = 100 // Load more when 100px from bottom

                if (scrollTop + clientHeight >= scrollHeight - threshold) {
                    candidatesQuery.fetchNextPage()
                }
            },
            [hasNextPage, isLoading, candidatesQuery]
        )

        useEffect(() => {
            const container = scrollContainerRef.current
            if (!container) return

            // Find the scrollable element (Card body)
            const cardBody = container.querySelector('.ant-card-body')
            if (!cardBody) return

            cardBody.addEventListener('scroll', handleScroll)
            return () => cardBody.removeEventListener('scroll', handleScroll)
        }, [handleScroll])

        const { t } = useTranslation()
        const { attributes, listeners, setNodeRef, transform, isDragging } = useSortable({
            id: `column-${id}`,
            data: { type: 'column', columnId: id },
        })

        const columnStyle = {
            width: 300,
            borderColor: isOver ? colorPrimary : undefined,
            transform: CSS.Transform.toString(transform),
            opacity: isDragging ? 0.5 : 1,
            flex: '0 0 auto',
        }

        const shouldShowDropLine = dropLinePosition && dropLinePosition.columnId === id

        // Use appropriate hooks based on mode
        const deleteCampaignColumnMutation = useDeleteCampaignColumn(campaignId || '', id)
        const deleteCompanyColumnMutation = useDeleteCompanyColumn(activeCompany?.id || '', id)
        const updateCampaignColumnMutation = useUpdateCampaignColumn(campaignId || '', id)
        const updateCompanyColumnMutation = useUpdateCompanyColumn(activeCompany?.id || '', id)
        const sortCampaignMutation = useSortColumnCandidates(campaignId || '', id)
        const sortCompanyMutation = useSortCompanyColumnCandidates(activeCompany?.id || '', id)
        const archiveCandidatesMutation = useArchiveCandidate()
        const { setSelected } = useBoardContext()

        const deleteMutation = campaignId ? deleteCampaignColumnMutation : deleteCompanyColumnMutation
        const sortMutation = campaignId ? sortCampaignMutation : sortCompanyMutation

        const handleSort = (sortKey: string, direction: string) => {
            const sortMapping = {
                score: 'compatibility',
                date: 'created_at',
                name: 'first_name',
            }

            const sortBy = sortMapping[sortKey as keyof typeof sortMapping] || sortKey

            sortMutation.mutate({ sortBy, direction })
        }

        return (
            <div
                {...attributes}
                {...listeners}
                ref={setNodeRef}
                style={columnStyle}
            >
                <div ref={scrollContainerRef}>
                    <Card
                        size="small"
                        styles={{
                            body: {
                                minWidth: '300px',
                                padding: '12px',
                                minHeight: 400,
                                maxHeight: height,
                                overflowY: 'auto',
                                cursor: 'grab',
                            },
                        }}
                    >
                        <div className="bg-white w-[93%] z-10 absolute top-0 pt-2 cursor-grab rounded-t-lg flex justify-between">
                            <Typography.Title
                                level={5}
                                className="text-sm mb-0 cursor-text"
                                contentEditable
                                spellCheck="false"
                                onClick={(event) => {
                                    event.stopPropagation()
                                }}
                                suppressContentEditableWarning
                                onBlur={(e) => {
                                    const newName = e.target.innerText
                                    const updatePromise = campaignId
                                        ? updateCampaignColumnMutation.mutateAsync({ name: newName })
                                        : updateCompanyColumnMutation.mutateAsync(newName)
                                    updatePromise.catch(() => {
                                        e.target.innerText = title
                                    })
                                }}
                            >
                                {title}
                            </Typography.Title>
                            <Dropdown
                                trigger={['click']}
                                placement="bottomRight"
                                menu={{
                                    items: [
                                        {
                                            key: 'newest-on-top',
                                            label: t('Newest on top'),
                                            onClick: () => handleSort('date', 'desc'),
                                        },
                                        {
                                            key: 'highest-score-on-top',
                                            label: t('Highest score on top'),
                                            onClick: () => handleSort('score', 'desc'),
                                        },
                                        {
                                            key: 'lowest-score-on-top',
                                            label: t('Lowest score on top'),
                                            onClick: () => handleSort('score', 'asc'),
                                        },
                                        {
                                            key: 'oldest-on-top',
                                            label: t('Oldest on top'),
                                            onClick: () => handleSort('date', 'asc'),
                                        },
                                        {
                                            key: 'archive-column-candidates',
                                            onClick: () => {
                                                if (window.confirm(t('Are you sure?'))) {
                                                    archiveCandidatesMutation
                                                        .mutateAsync({
                                                            candidates: items.map((item) => item.id),
                                                        })
                                                        .then(() => {
                                                            setSelected((selected) => {
                                                                const newSelected = { ...selected }

                                                                items.forEach((item) => delete newSelected[item.id])

                                                                return newSelected
                                                            })
                                                        })
                                                }
                                            },
                                            label: t('Archive candidates'),
                                        },
                                        {
                                            key: 'delete',
                                            label: (
                                                <Tooltip
                                                    title={
                                                        items.length > 0
                                                            ? t('Can not delete column that still contains candidates')
                                                            : undefined
                                                    }
                                                >
                                                    {t('Delete')}
                                                </Tooltip>
                                            ),
                                            disabled: items.length > 0,
                                        },
                                    ],
                                }}
                            >
                                <Button
                                    size="small"
                                    type="text"
                                    icon={<EllipsisOutlined />}
                                />
                            </Dropdown>
                        </div>
                        <Spin
                            spinning={candidatesQuery.isPending}
                            indicator={<LoadingOutlined spin />}
                            className="py-12"
                        >
                            <div className="mt-6 overflow-y-hidden">
                                <SortableContext
                                    items={items.map((item) => item.id)}
                                    strategy={verticalListSortingStrategy}
                                >
                                    {shouldShowDropLine && dropLinePosition.index === 0 && <DropCard />}
                                    {items.map((item, index) => (
                                        <React.Fragment key={item.id}>
                                            <MemoizedSortableCard
                                                item={item}
                                                columnId={id}
                                                index={index}
                                                collapsed={!showAttributes}
                                            />
                                            {shouldShowDropLine && dropLinePosition.index === index + 1 && <DropCard />}
                                        </React.Fragment>
                                    ))}
                                </SortableContext>

                                {!candidatesQuery.isPending &&
                                    !shouldShowDropLine &&
                                    items.length === 0 &&
                                    !['conversation', 'finished'].includes(id) && (
                                        <Flex
                                            className="w-full my-12"
                                            justify="center"
                                        >
                                            <Button
                                                onClick={() => deleteMutation.mutate()}
                                                loading={deleteMutation.isPending}
                                                icon={<DeleteOutlined />}
                                            >
                                                {t('Delete column')}
                                            </Button>
                                        </Flex>
                                    )}
                            </div>
                        </Spin>
                    </Card>
                </div>
            </div>
        )
    },
    (prevProps, nextProps) =>
        prevProps.id === nextProps.id &&
        prevProps.title === nextProps.title &&
        prevProps.isOver === nextProps.isOver &&
        prevProps.dropLinePosition === nextProps.dropLinePosition
)
